import React from 'react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Check, X, Edit2, Trash2, Plus } from 'lucide-react';
import DatasetSearchDropdown from './DatasetSearchDropdown';
import { qualityOptions } from './constants';

function getQualityBadge(quality) {
  const colors = {
    high: 'bg-emerald-100 text-emerald-700',
    medium: 'bg-amber-100 text-amber-700',
    proxy: 'bg-red-100 text-red-700'
  };
  return colors[quality] || colors.medium;
}

function getDisplayValue(item, col) {
  if (col.key === 'quality') return item.data_quality;
  if (col.key === 'distance_km' || col.key === 'load_kg' ||
      col.key === 'yield_percent' || col.key === 'scrap_percent') {
    return item.additional_data?.[col.key];
  }
  // G2G specific fields
  if (col.key === 'usage_basis') {
    const basisMap = { 'per_year': 'Per Year', 'per_hour': 'Per Hour', 'per_cycle': 'Per Cycle' };
    return basisMap[item.usage_basis] || item.usage_basis;
  }
  if (col.key === 'replacement_interval_years') {
    const interval = item.replacement_interval_years;
    const count = item.derived_replacement_count;
    return interval ? `${interval}y (${count || 0} replacements)` : '-';
  }
  if (col.key === 'eol_method') {
    const methodMap = { 'cut_off': 'Cut-off', 'substitution': 'Substitution', 'allocation': 'Allocation' };
    return methodMap[item.eol_method] || item.eol_method || '-';
  }
  return item[col.key];
}

function renderCell(col, value, isEditing, data, setData, item, { datasets, activeTab, handleDatasetSelect }) {
  if (!isEditing) {
    if (col.type === 'quality') {
      return (
        <span className={`px-2 py-1 rounded text-xs font-medium ${getQualityBadge(value)}`}>
          {value || 'medium'}
        </span>
      );
    }
    if (col.type === 'dataset-search') {
      return (
        <div>
          <span className="font-medium text-slate-900">{value || '-'}</span>
          {item?.additional_data?.dataset_library && (
            <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 font-medium">
              {item.additional_data.dataset_library}
            </span>
          )}
          {item?.emission_factor != null && (
            <span className="ml-2 text-xs text-slate-500 font-mono">
              (EF: {item.emission_factor})
            </span>
          )}
        </div>
      );
    }
    if (col.align === 'right') {
      return <span className="font-mono text-slate-700">{value || '-'}</span>;
    }
    return <span className="text-slate-700">{value || '-'}</span>;
  }

  // Edit mode
  if (col.type === 'dataset-search') {
    return (
      <DatasetSearchDropdown
        datasets={datasets}
        value={data.name || ''}
        category={activeTab}
        placeholder={col.placeholder}
        onChange={(ds) => handleDatasetSelect(ds, setData, data)}
      />
    );
  }
  if (col.type === 'select') {
    return (
      <Select value={data[col.key] || ''} onValueChange={(v) => setData({...data, [col.key]: v})}>
        <SelectTrigger className="h-8 text-sm bg-white">
          <SelectValue placeholder="Select..." />
        </SelectTrigger>
        <SelectContent className="bg-white">
          {col.options.map(opt => (
            <SelectItem key={opt} value={opt}>{opt}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }
  if (col.type === 'quality') {
    return (
      <Select value={data.data_quality || 'medium'} onValueChange={(v) => setData({...data, data_quality: v})}>
        <SelectTrigger className="h-8 text-sm bg-white">
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="bg-white">
          {qualityOptions.map(q => (
            <SelectItem key={q} value={q} className="capitalize">{q}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }
  return (
    <Input
      type={col.type === 'number' ? 'number' : 'text'}
      value={data[col.key] || ''}
      onChange={(e) => setData({...data, [col.key]: e.target.value})}
      placeholder={col.placeholder}
      className={`h-8 text-sm bg-white ${col.align === 'right' ? 'text-right' : ''}`}
    />
  );
}

export default function InventoryTable({
  config,
  filteredInventory,
  isLocked,
  editingRowId,
  editingData,
  setEditingData,
  newRow,
  setNewRow,
  datasets,
  activeTab,
  onStartEditing,
  onCancelEditing,
  onSaveEditing,
  onDeleteItem,
  onSaveNewRow,
  onCancelNewRow,
  onAddNewRow,
  handleDatasetSelect
}) {
  const renderCtx = { datasets, activeTab, handleDatasetSelect };

  return (
    <>
      <div className="rounded-lg border border-slate-200">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              {config.columns.map((col) => (
                <th
                  key={col.key}
                  className={`px-3 py-3 font-semibold text-slate-600 whitespace-nowrap ${col.align === 'right' ? 'text-right' : 'text-left'}`}
                >
                  {col.label}
                  {col.required && <span className="text-red-500 ml-1">*</span>}
                </th>
              ))}
              {!isLocked && <th className="px-3 py-3 w-24 text-right text-slate-600 font-semibold">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {/* Existing rows */}
            {filteredInventory.map((item) => {
              const isEditing = editingRowId === item.id;
              return (
                <tr key={item.id} className={`border-b border-slate-100 ${isEditing ? 'bg-sky-50' : 'hover:bg-slate-50'}`}>
                  {config.columns.map((col) => (
                    <td key={col.key} className={`px-3 py-2 ${col.align === 'right' ? 'text-right' : ''} ${col.type === 'dataset-search' ? 'relative overflow-visible' : ''}`}>
                      {renderCell(
                        col,
                        getDisplayValue(item, col),
                        isEditing,
                        editingData,
                        setEditingData,
                        item,
                        renderCtx
                      )}
                    </td>
                  ))}
                  {!isLocked && (
                    <td className="px-3 py-2 text-right">
                      {isEditing ? (
                        <div className="flex items-center justify-end gap-1">
                          <Button size="icon" variant="ghost" onClick={() => onSaveEditing(item)} className="h-7 w-7 text-emerald-600 hover:bg-emerald-50">
                            <Check className="h-4 w-4" />
                          </Button>
                          <Button size="icon" variant="ghost" onClick={onCancelEditing} className="h-7 w-7 text-slate-500 hover:bg-slate-100">
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ) : (
                        <div className="flex items-center justify-end gap-1">
                          <Button size="icon" variant="ghost" onClick={() => onStartEditing(item)} className="h-7 w-7 text-slate-500 hover:bg-slate-100">
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button size="icon" variant="ghost" onClick={() => onDeleteItem(item.id)} className="h-7 w-7 text-red-500 hover:bg-red-50">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}

            {/* New row being added */}
            {newRow && (
              <tr className="bg-emerald-50 border-b border-emerald-200">
                {config.columns.map((col) => (
                  <td key={col.key} className={`px-3 py-2 ${col.align === 'right' ? 'text-right' : ''} ${col.type === 'dataset-search' ? 'relative overflow-visible' : ''}`}>
                    {renderCell(col, newRow[col.key], true, newRow, setNewRow, null, renderCtx)}
                  </td>
                ))}
                <td className="px-3 py-2 text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Button size="icon" variant="ghost" onClick={onSaveNewRow} className="h-7 w-7 text-emerald-600 hover:bg-emerald-100">
                      <Check className="h-4 w-4" />
                    </Button>
                    <Button size="icon" variant="ghost" onClick={onCancelNewRow} className="h-7 w-7 text-slate-500 hover:bg-slate-100">
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </td>
              </tr>
            )}

            {/* Empty state */}
            {filteredInventory.length === 0 && !newRow && (
              <tr>
                <td colSpan={config.columns.length + (isLocked ? 0 : 1)} className="px-4 py-8 text-center text-slate-500">
                  No items added yet. {!isLocked && 'Click "Add Item" to get started.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Add Item Button — search spans the shared reference library (BAFU)
          plus legacy/user datasets, so it is always available. */}
      {!isLocked && !newRow && (
        <div className="mt-4">
          <Button variant="outline" onClick={onAddNewRow} data-testid="add-item-btn" className="w-full border-dashed border-slate-300 text-slate-600 hover:bg-slate-50">
            <Plus className="h-4 w-4 mr-2" />
            Add Item from Dataset Library
          </Button>
        </div>
      )}
    </>
  );
}
